import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=logged
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Not authenticated or server unreachable — clear user state.
      // 401 is expected on first load before login; only log unexpected errors.
      if (err?.response?.status && err.response.status !== 401) {
        console.error("Auth refresh failed:", err);
      }
      setUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (username, password) => {
    // Backend sets httpOnly cookies as the primary auth mechanism.
    // We rely on cookies only — no localStorage token storage (XSS-safe).
    const { data } = await api.post("/auth/login", { username, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network failure on logout is non-fatal; the cookies will be invalidated
      // on next 401. Log for observability.
      console.error("Logout request failed:", err);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, checked, login, logout, refresh }),
    [user, checked, login, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export function ProtectedRoute({ children, adminOnly = false }) {
  const { user, checked } = useAuth();
  const location = useLocation();
  const navState = useMemo(() => ({ from: location }), [location]);
  if (!checked) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-slate-400 text-sm tracking-wider uppercase">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={navState} replace />;
  if (adminOnly && user.group !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}
